/**
 * Pure helpers for "Changes by person" (components/collab/ChangesPanel.jsx) over vfs-service's
 * `GET /collab/changes` answer (services/CollabService.js): `{ you, changes, runs, authors }`, changes newest first,
 * each `{ sha, timestamp, message, author, via, aiModel, agent, runId, files: [{ path, added, removed, status }],
 * canRevert }`. An agent's change is credited to the person who drove it (`author`), with the agent in `via`.
 */

const DAY_MS = 24 * 3600 * 1000;

/** The `since` of a range: 'today' (since midnight), '7d', or 'all' (undefined). */
export function sinceFor(range, now = new Date()) {
  if (range === 'today') {
    const midnight = new Date(now);
    midnight.setHours(0, 0, 0, 0);
    return midnight.toISOString();
  }
  if (range === '1h') { return new Date(now.getTime() - 3600_000).toISOString(); }
  if (range === '7d') { return new Date(now.getTime() - 7 * DAY_MS).toISOString(); }
  if (range === '30d') { return new Date(now.getTime() - 30 * DAY_MS).toISOString(); }
  return undefined;
}

export const firstNameOf = (author) => String(author?.name || 'Someone').split(/\s+/)[0];

const isYou = (author, you) => Boolean(you?.id && author?.id === you.id);

/** "You", "Luca Bianchi", "Flow chat for you", "Claude Code for Luca". */
export function whoLabel({ author, via }, you) {
  if (!via) { return isYou(author, you) ? 'You' : (author?.name || 'Someone'); }
  const label = via === 'Flow' ? 'Flow chat' : via;
  return `${label} for ${isYou(author, you) ? 'you' : firstNameOf(author)}`;
}

/** The key of a person-or-agent: the credited person plus the agent (empty for their own typing). */
export const authorKey = (change) => `${change?.author?.id || 'unknown'}|${change?.via || ''}`;

function filesOf(changes) {
  const byPath = new Map();
  changes.forEach((change) => {
    (change.files || []).forEach((file) => {
      const entry = byPath.get(file.path) || { path: file.path, added: 0, removed: 0, created: false, deleted: false, changes: 0 };
      entry.added += file.added || 0;
      entry.removed += file.removed || 0;
      entry.changes += 1;
      if (file.status === 'A') { entry.created = true; }
      if (file.status === 'D') { entry.deleted = true; }
      byPath.set(file.path, entry);
    });
  });
  return [...byPath.values()].sort((a, b) => a.path.localeCompare(b.path));
}

function groupOf(key, changes, extra = {}) {
  const files = filesOf(changes);
  const times = changes.map((change) => change.timestamp).filter(Boolean).sort();
  return {
    key,
    changes,
    files,
    shas: changes.map((change) => change.sha),
    added: files.reduce((n, f) => n + f.added, 0),
    removed: files.reduce((n, f) => n + f.removed, 0),
    from: times[0] || null,
    to: times[times.length - 1] || null,
    canRevert: changes.length > 0 && changes.every((change) => change.canRevert),
    ...extra,
  };
}

/** Changes left after the author filter (a set of author keys; empty: everyone) and "only files I also touched". */
export function filterChanges(changes, { authors = [], onlyMine = false, you = null } = {}) {
  let out = changes || [];
  if (authors.length > 0) { out = out.filter((change) => authors.includes(authorKey(change))); }
  if (onlyMine && you?.id) {
    const mine = new Set();
    (changes || []).filter((change) => change.author?.id === you.id).forEach((change) => (change.files || []).forEach((f) => mine.add(f.path)));
    out = out.filter((change) => (change.files || []).some((f) => mine.has(f.path)));
  }
  return out;
}

/**
 * Groups for the panel. `by`:
 * - 'person': one group per person and per agent they drove (`kind` person|agent);
 * - 'run': one group per agent run, and each person's own typing as one group;
 * - 'file': one group per file, with who touched it.
 * Groups are ordered by their latest change, newest first.
 */
export function groupChanges(changes, by = 'person') {
  const list = changes || [];
  const buckets = new Map();
  const add = (key, change, make) => {
    if (!buckets.has(key)) { buckets.set(key, { make, changes: [] }); }
    buckets.get(key).changes.push(change);
  };
  list.forEach((change) => {
    if (by === 'file') {
      (change.files || []).forEach((file) => add(`file:${file.path}`, change, () => ({ kind: 'file', path: file.path })));
      return;
    }
    if (by === 'run' && change.via) {
      add(change.runId || `run:${change.sha}`, change, () => ({ kind: 'run', author: change.author, via: change.via, aiModel: change.aiModel, runId: change.runId }));
      return;
    }
    add(authorKey(change), change, () => ({ kind: change.via ? 'agent' : 'person', author: change.author, via: change.via || null, aiModel: change.aiModel || null }));
  });
  const groups = [...buckets.entries()].map(([key, { make, changes: grouped }]) => {
    const group = groupOf(key, grouped, make());
    if (by === 'file') {
      const who = new Map();
      grouped.forEach((change) => who.set(authorKey(change), { author: change.author, via: change.via || null }));
      group.who = [...who.values()];
      // The file's own lines only.
      const own = group.files.find((f) => f.path === group.path);
      group.added = own ? own.added : 0;
      group.removed = own ? own.removed : 0;
    }
    return group;
  });
  return groups.sort((a, b) => String(b.to).localeCompare(String(a.to)));
}

/** Chips for the author filter: `{ key, kind, author, via, count }`, the vfs answer's `authors` or computed. */
export function authorChips(data) {
  if (Array.isArray(data?.authors) && data.authors.length > 0) { return data.authors; }
  const chips = new Map();
  (data?.changes || []).forEach((change) => {
    const key = authorKey(change);
    const chip = chips.get(key) || { key, kind: change.via ? 'agent' : 'person', author: change.author, via: change.via || null, count: 0 };
    chip.count += 1;
    chips.set(key, chip);
  });
  return [...chips.values()].sort((a, b) => b.count - a.count);
}

/** The revert button's words for a group, or null when the caller may not revert it. */
export function revertLabel(group, you) {
  if (!group || !group.canRevert || group.kind === 'file') { return null; }
  if (group.kind === 'run') { return 'Revert this run…'; }
  if (isYou(group.author, you)) { return group.via ? `Revert ${whoLabel(group, you)}…` : 'Revert mine…'; }
  return group.via ? `Revert ${whoLabel(group, you)}…` : `Revert ${firstNameOf(group.author)}’s changes…`;
}

/** "3 changes", "1 change". */
export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "09:12 to 10:31" (same day) or "Mon 09:12 to today 10:31". */
export function timeRange(from, to, now = new Date()) {
  if (!from) { return ''; }
  const fmt = (iso) => {
    const date = new Date(iso);
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toDateString() === now.toDateString() ? time : `${date.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' })} ${time}`;
  };
  return from === to || !to ? fmt(from) : `${fmt(from)} to ${fmt(to)}`;
}

/**
 * One file's part of a `git show` text: its hunk lines as `{ kind: 'add'|'del'|'ctx'|'hunk', text, oldLine, newLine }`.
 * `repoPath` is the path inside the repo (`src/App.jsx` for `services/web/src/App.jsx`); null keeps every file.
 */
export function diffLinesFor(showText, repoPath = null) {
  const lines = String(showText || '').split('\n');
  const out = [];
  let inFile = false;
  let oldLine = 0;
  let newLine = 0;
  lines.forEach((line) => {
    if (line.startsWith('diff --git ')) {
      const match = / b\/(.+)$/.exec(line);
      inFile = !repoPath || Boolean(match && (match[1] === repoPath || repoPath.endsWith(`/${match[1]}`)));
      return;
    }
    if (!inFile) { return; }
    if (line.startsWith('@@')) {
      const match = /@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/.exec(line);
      oldLine = match ? Number(match[1]) : 0;
      newLine = match ? Number(match[2]) : 0;
      out.push({ kind: 'hunk', text: line });
      return;
    }
    if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('index ') || line.startsWith('new file') || line.startsWith('deleted file')) { return; }
    if (line.startsWith('+')) { out.push({ kind: 'add', text: line.slice(1), newLine }); newLine += 1; return; }
    if (line.startsWith('-')) { out.push({ kind: 'del', text: line.slice(1), oldLine }); oldLine += 1; return; }
    if (line.startsWith(' ')) { out.push({ kind: 'ctx', text: line.slice(1), oldLine, newLine }); oldLine += 1; newLine += 1; }
  });
  return out;
}

/** The path inside its repo: `services/web/src/App.jsx` → `src/App.jsx`, `brain/x.md` → `x.md`. */
export function repoPathOf(path) {
  const parts = String(path || '').split('/');
  if (parts[0] === 'services' && parts.length > 2) { return parts.slice(2).join('/'); }
  if (parts[0] === 'brain' && parts.length > 1) { return parts.slice(1).join('/'); }
  return path;
}
